import {
  ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, styled } from '@mui/material';

import ChatIcon from '@/assets/images/svgs/circled-email.svg';
import ExclamationIcon from '@/assets/images/svgs/circled-exclemation.svg';
import LocationIcon from '@/assets/images/svgs/circled-marker.svg';
import { colors, sizes } from '@/theme/variables';
import { getNewTabSearch } from '@/utils';

import { useTabLayoutContext } from '../TabLayoutContext';

interface AdditionalTabInfo {
  count?: number;
  showWarning?: boolean;
  iconClass?: string;
}

const RootStack = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  padding: `${sizes.bottomTabBar.padding}px`,
  bottom: `${sizes.bottomTabBar.margin}px`,
  zIndex: '1',
  justifyContent: 'flex-start',
  overflowX: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  '& .tab_button': {
    position: 'relative',
    textDecoration: 'none',
    padding: '4px 12px',
    fontWeight: 500,
    fontSize: 15,
    color: theme.palette.text.primary,
    minWidth: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '& .tab_icon': {
      color: 'inherit',
      fontSize: 22,
      flexShrink: 0,
    },
    '& .tab_meta': {
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      marginLeft: 2,
    },
    '& .tab_count_badge': {
      width: 22,
      height: 22,
      borderRadius: '50%',
      backgroundColor: colors.error.main,
      color: colors.white,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1,
    },
    '& .tab_warning_badge': {
      width: 10,
      height: 10,
      borderRadius: '50%',
      backgroundColor: colors.grey[70],
      color: colors.white,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 8,
      fontWeight: 700,
      lineHeight: 1,
      position: 'absolute',
      top: -1,
      right: -2,
      border: `1px solid ${theme.palette.background.paper}`,
    },
    '& .tab_meta_icon': {
      width: 16,
      height: 16,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: 4,
      '& > svg': {
        position: 'absolute',
        top: '-14px',
        left: '12px',
        width: 21,
        height: 21,
      },
    },
    '& .meta_icon--both > svg': {
      filter: 'drop-shadow(-2px 2px #fff)',
    },
    '&.active': {
      color: colors.error.main,
    },
  },
  '& .tab_button.has_separator': {
    borderRadius: 0,
    marginLeft: 8,
    paddingLeft: 20,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      width: 1,
      height: 36,
      backgroundColor: colors.grey[10],
      transform: 'translateY(-50%)',
    },
  },
  '&.topBar': {
    alignItems: 'center',
    borderRadius: 0,
    background: 'none',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: 0,
    height: 50,
    '& .tab_button': {
      padding: '5px 0',
      marginRight: '20px',
      color: theme.palette.text.secondary,
      alignSelf: 'stretch',
      '&.active': {
        color: theme.palette.text.primary,
        '&:after': {
          content: '""',
          position: 'absolute',
          width: '100%',
          background: colors.error.main,
          height: 2,
          bottom: 0,
        },
      },
    },
    '& .tab_button.has_separator': {
      marginLeft: 0,
      paddingLeft: 0,
      '&::before': {
        content: 'none',
      },
    },
  },
}));

const ScrollIndicator = styled(Box)<{ side: 'left' | 'right' }>(({ side }) => ({
  position: 'absolute',
  [side]: 0,
  top: 0,
  bottom: 0,
  width: 48,
  background:
    side === 'right'
      ? 'linear-gradient(to left, rgba(255,255,255,1) 30%, rgba(255,255,255,0) 100%)'
      : 'linear-gradient(to right, rgba(255,255,255,1) 30%, rgba(255,255,255,0) 100%)',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: side === 'right' ? 'flex-end' : 'flex-start',
}));

interface BottomTabBarProps extends ComponentPropsWithoutRef<typeof Stack> {
  additionalInfo?: AdditionalTabInfo[];
  additionalInfoByPath?: Record<string, AdditionalTabInfo>;
  execOnRerender?: () => void;
  isQuickView?: boolean;
  cfsId?: string;
}

export const BottomTabBar = ({
  additionalInfo,
  additionalInfoByPath,
  isQuickView,
  cfsId,
  children,
  execOnRerender,
  ...rest
}: BottomTabBarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    tabLinks,
    currentNavTab,
    basePath,
    rerender,
    rerenderTabLayout,
    updateTabLayoutContextValues,
  } = useTabLayoutContext();

  const visibleTabLinks = tabLinks.filter((tab) => !tab.hidden);

  useEffect(() => {
    if (!rerender) return;
    try {
      execOnRerender?.();
    } finally {
      rerenderTabLayout(false);
    }
  }, [rerender, execOnRerender, rerenderTabLayout]);

  const onClick = (path: string) => {
    const normalizedBasePath = basePath.endsWith('/')
      ? basePath.slice(0, -1)
      : basePath;
    const safeBasePath = normalizedBasePath || '/';
    const searchParams = new URLSearchParams(window.location.search);
    const search = getNewTabSearch(searchParams);

    // Preserve search query param across tab navigation
    const searchValue = searchParams.get('search');
    const searchParam = searchValue
      ? `${search ? '&' : '?'}search=${encodeURIComponent(searchValue)}`
      : '';

    if (isQuickView && cfsId) {
      navigate(`/cfs-active/${cfsId}/${path}${search}${searchParam}`);
    } else {
      if (
        path === currentNavTab?.path &&
        !location.pathname.includes('cases')
      ) {
        navigate(`${safeBasePath}${search}${searchParam}`);
        return;
      }
      updateTabLayoutContextValues({
        nextPath: location.pathname,
        currentScreen: undefined,
      });
      navigate(`${safeBasePath}/${path}${search}${searchParam}`);
    }
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState, visibleTabLinks.length]);

  const scrollBy = (direction: 'left' | 'right') => {
    scrollRef.current?.scrollBy({
      left: direction === 'right' ? 200 : -200,
      behavior: 'smooth',
    });
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {canScrollLeft && (
        <ScrollIndicator side="left">
          <IconButton
            size="small"
            onClick={() => scrollBy('left')}
            sx={{ color: colors.grey[70] }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
        </ScrollIndicator>
      )}
      {canScrollRight && (
        <ScrollIndicator side="right">
          <IconButton
            size="small"
            onClick={() => scrollBy('right')}
            sx={{ color: colors.grey[70] }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </ScrollIndicator>
      )}
      <RootStack ref={scrollRef} flexDirection="row" {...rest}>
        {visibleTabLinks.map((link, index) => {
          const tabInfo =
            additionalInfoByPath?.[link.path] ?? additionalInfo?.[index];
          const count = tabInfo?.count;
          const hasCount = typeof count === 'number' && count > 0;
          const showWarning = !!tabInfo?.showWarning && hasCount;
          const Icon = link.icon;

          return (
            <Button
              key={link.path}
              className={`tab_button ${
                currentNavTab?.path === link.path ? 'active' : ''
              } ${index > 0 ? 'has_separator' : ''}`}
              variant="text"
              onClick={() => onClick(link.path)}
            >
              {Icon && <Icon className="tab_icon" />}
              {link.label}
              {(hasCount || showWarning || tabInfo?.iconClass) && (
                <Box className="tab_meta">
                  {hasCount && <Box className="tab_count_badge">{count}</Box>}
                  {showWarning && <Box className="tab_warning_badge">!</Box>}
                  {tabInfo?.iconClass && (
                    <Box className={`tab_meta_icon ${tabInfo.iconClass}`}>
                      {tabInfo.iconClass === 'meta_icon--location' && (
                        <LocationIcon />
                      )}
                      {tabInfo.iconClass === 'meta_icon--message' && (
                        <ChatIcon />
                      )}
                      {tabInfo.iconClass === 'meta_icon--both' && (
                        <ExclamationIcon />
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Button>
          );
        })}
        {children}
      </RootStack>
    </Box>
  );
};
